import { Controller } from "@hotwired/stimulus"
import { hideIntercom, showIntercom } from "../helpers/intercom"

const SVG_PENCIL_SQUARE = '<svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"/></svg>'
const SVG_CHECK = '<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>'
const SVG_INFO_CIRCLE = '<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/></svg>'

export default class extends Controller {
  static targets = [
    "newFaModal", "modalTitle", "faTitle", "faPm", "faPo", "poRow",
    "faLineItems", "faModalTotal", "faModalDaysTotal",
    "faModalContractTotal", "faModalPrevTotal", "faModalFaTotalsTotal",
    "deleteModal", "signModal", "unsignModal", "requestApprovalModal",
    "faDropzone", "faFileInput", "faFileList",
    "addPastFaBtn", "saveDraftBtn", "signRequestBtn",
    "pastFaConfirmModal", "pastFaSequenceLabel",
    "faHistoryLog", "faHistoryLogEntries",
    "modalRequestApprovalBtn", "modalSignedBadge", "modalSignedText", "modalSignedTooltip", "modalSignedCheck",
    "unsignBtn", "modalPendingLabel", "modalDeleteBtn", "modalEditPastBtn", "modalApproveBtn", "modalRejectBtn",
    "rejectFaModal", "rejectComment",
    "overallTotals", "overallAmount", "overallDays", "faCards", "emptyState",
    "uploadSection", "uploadLabel"
  ]

  static values = {
    projectId: Number,
    currency: String,
    currencyFormat: String,
    delimiter: String,
    separator: String,
    isOwner: Boolean,
    isOtherVendor: Boolean,
    feeScheduleApproved: Boolean,
    hasPo: Boolean,
    ovid: Number
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._editSequence = null
    this._isPast = false
    this._pendingDeleteSequence = null
    this._pendingSignSequence = null
    this._lineItemsData = []
    this._attachmentIds = []
    this._pendingUploads = 0
    this._previousTotals = {}
    this._signFromEditModal = false
    this._signFromEditIsNew = false
    this._signFromEditSaveResp = null
    this._approveAfterAcknowledge = false
  }

  // ── Currency helpers ──────────────────────────────────────────────

  _parseCurrency(str) {
    if (!str) return 0
    let cleaned = str.replace(this.currencyValue, "").trim()
    let neg = false
    if (cleaned.startsWith("(") && cleaned.endsWith(")")) {
      neg = true
      cleaned = cleaned.slice(1, -1)
    }
    if (this.currencyFormatValue === "european_currency_format") {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      cleaned = cleaned.replace(/'/g, "")
    } else {
      cleaned = cleaned.replace(/,/g, "")
    }
    const val = parseFloat(cleaned) || 0
    return neg ? -val : val
  }

  _formatCurrency(num) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    let formatted
    if (this.currencyFormatValue === "european_currency_format") {
      formatted = abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
    if (num < 0) return `${this.currencyValue}(${formatted})`
    return `${this.currencyValue}${formatted}`
  }

  _formatAmount(num) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    let formatted
    if (this.currencyFormatValue === "european_currency_format") {
      formatted = abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
    if (num < 0) return `(${formatted})`
    return formatted
  }

  // ── Vendor selector ──────────────────────────────────────────────

  changeVendor(event) {
    const ovid = event.target.value
    const url = new URL(window.location)
    url.searchParams.set("ovid", ovid)
    window.location = url.toString()
  }

  // ── New FA ────────────────────────────────────────────────────────

  async newFeeAdjustment() {
    if (!this.feeScheduleApprovedValue) {
      this._showToast("Fee schedule must be approved before creating a fee adjustment.", "alert")
      return
    }
    this._editSequence = null
    this._isPast = false
    this.modalTitleTarget.textContent = "New Fee Adjustment"
    await this._loadLineItemsAndShowModal()
  }

  async newPastFeeAdjustment() {
    if (!this.feeScheduleApprovedValue) {
      this._showToast("Fee schedule must be approved before creating a fee adjustment.", "alert")
      return
    }
    this._editSequence = null
    this._isPast = true
    this.modalTitleTarget.textContent = "Past Fee Adjustment"
    await this._loadLineItemsAndShowModal()
  }

  async editFeeAdjustment(event) {
    const sequence = event.currentTarget.dataset.sequence
    this._editSequence = sequence
    const card = this.element.querySelector(`.fee-adjustment[data-sequence="${sequence}"]`)
    this._isPast = card?.dataset.isPast === "true"
    this.modalTitleTarget.textContent = this._isPast ? `Past Fee Adjustment #${sequence}` : `Edit Fee Adjustment #${sequence}`
    await this._loadLineItemsAndShowModal(sequence)
  }

  async _loadLineItemsAndShowModal(sequence = null) {
    const url = `/projects/${this.projectIdValue}/finances/new_ov_fee_adjustment_modal_data${sequence ? `?sequence=${sequence}&ovid=${this.ovidValue}` : `?ovid=${this.ovidValue}`}`
    const resp = await fetch(url, { headers: { "Accept": "application/json" } })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      this._showToast(err.error || "An error occurred.", "alert")
      return
    }
    const data = await resp.json()

    this._lineItemsData = data.line_items
    this._previousTotals = data.previous_totals || {}
    this._attachmentIds = []
    this._pendingUploads = 0

    this.faTitleTarget.value = data.fa_data?.title || ""
    this.faPmTarget.value = data.fa_data?.project_manager || data.project_manager || ""
    this.faPoTarget.value = data.fa_data?.po_number || data.po_number || ""

    // Clear file list
    if (this.hasFaFileListTarget) this.faFileListTarget.innerHTML = ""

    this._renderFaLineItems(data.line_items, data.fa_data?.items || [], this._previousTotals)

    // Load existing attachments when editing
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach(att => {
        this._attachmentIds.push(att.id)
        const chip = this._createFileChip(att.filename, {
          removable: true,
          url: att.url,
          onRemove: () => {
            chip.remove()
            this._attachmentIds = this._attachmentIds.filter(id => id !== att.id)
          }
        })
        if (this.hasFaFileListTarget) this.faFileListTarget.appendChild(chip)
      })
    }

    // Render history log
    this._renderHistoryLog(data.history_log || [])

    // Render modal header badges (signatures + request approval)
    this._renderModalHeaderBadges(data)

    // Show/hide Add Past FA button (visible when creating new FA — both regular and past)
    if (this.hasAddPastFaBtnTarget) {
      this.addPastFaBtnTarget.classList.toggle("hidden", !!this._editSequence)
    }

    this.newFaModalTarget.classList.remove("hidden")
    hideIntercom()
  }

  _renderFaLineItems(lineItems, existingItems, previousTotals) {
    const existing = {}
    existingItems.forEach(item => { existing[item.line_item_id] = item })
    const cur = this.currencyValue

    let html = ""
    lineItems.forEach(li => {
      const ex = existing[li.id]
      const prevTotal = parseFloat(previousTotals[li.id]) || 0
      const currentAmount = ex ? (parseFloat(ex.amount) || 0) : 0
      const faTotal = Math.round((prevTotal + currentAmount) * 100) / 100

      html += `
        <tr data-line-item-id="${li.id}">
          <td class="px-2 py-1.5 border-r border-gray-200 text-[13px] truncate">${this._escAttr(li.item || "")}</td>
          <td class="px-2 py-1.5 border-r border-gray-200 text-[13px] truncate">${this._escAttr(li.work || "")}</td>
          <td class="px-0 py-0 border-r border-gray-200 bg-amber-50">
            <input type="text" value="${this._escAttr(ex?.comment || "")}" data-field="comment"
              class="w-full px-2 py-1.5 text-[13px] bg-transparent focus:outline-none focus:ring-1 focus:ring-brand">
          </td>
          <td class="px-0 py-0 border-r border-gray-200">
            <div class="flex items-center">
              <span class="pl-2 text-gray-400 select-none text-[13px]">${cur}</span>
              <span class="w-full px-1 py-1.5 pr-2 text-right text-[13px]" data-field="contract">${this._formatAmount(li.fee || 0)}</span>
            </div>
          </td>
          <td class="px-0 py-0 border-r border-gray-200">
            <div class="flex items-center">
              <span class="pl-2 text-gray-400 select-none text-[13px]">${cur}</span>
              <span class="w-full px-1 py-1.5 pr-2 text-right text-[13px]" data-field="prev-total" data-prev-total="${prevTotal}">${this._formatAmount(prevTotal)}</span>
            </div>
          </td>
          <td class="px-0 py-0 border-r border-gray-200 bg-amber-50">
            <div class="flex items-center">
              <span class="pl-2 text-gray-400 select-none text-[13px]">${cur}</span>
              <input type="text" value="${ex ? this._formatAmount(currentAmount) : ""}" data-field="amount"
                data-action="input->ov-fee-adjustment#recalcRowTotal"
                class="w-full px-1 py-1.5 pr-2 text-right text-[13px] bg-transparent focus:outline-none">
            </div>
          </td>
          <td class="px-0 py-0 border-r border-gray-200">
            <div class="flex items-center">
              <span class="pl-2 text-gray-400 select-none text-[13px]">${cur}</span>
              <span class="w-full px-1 py-1.5 pr-2 text-right text-[13px]${faTotal < 0 ? " text-red-600" : ""}" data-field="fa-total">${this._formatAmount(faTotal)}</span>
            </div>
          </td>
          <td class="px-0 py-0 bg-amber-50">
            <input type="text" value="${this._escAttr(ex?.days || "")}" data-field="days"
              data-action="input->ov-fee-adjustment#recalcDays"
              class="w-full px-2 py-1.5 text-[13px] text-right bg-transparent focus:outline-none">
          </td>
        </tr>`
    })
    this.faLineItemsTarget.innerHTML = html
    this._recalcFooterTotals()
  }

  _escAttr(str) {
    return String(str).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  // ── Calculation ─────────────────────────────────────────────────

  recalcRowTotal(event) {
    const row = event.target.closest("tr")
    this._updateRowTotal(row)
    this._recalcFooterTotals()
  }

  recalcDays() {
    this._recalcFooterTotals()
  }

  // Keep for backwards compatibility
  recalcFaTotal() { this._recalcFooterTotals() }
  recalcFaDaysTotal() { this._recalcFooterTotals() }

  _updateRowTotal(row) {
    const prevTotal = parseFloat(row.querySelector("[data-prev-total]")?.dataset.prevTotal) || 0
    const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
    const total = Math.round((prevTotal + amount) * 100) / 100
    const totalSpan = row.querySelector("[data-field='fa-total']")
    if (totalSpan) {
      totalSpan.textContent = this._formatAmount(total)
      totalSpan.classList.toggle("text-red-600", total < 0)
    }
  }

  _recalcFooterTotals() {
    let contractTotal = 0, prevTotal = 0, amountTotal = 0, faTotalsTotal = 0, daysTotal = 0

    this.faLineItemsTarget.querySelectorAll("tr").forEach(row => {
      contractTotal += this._parseCurrency(row.querySelector("[data-field='contract']")?.textContent)
      prevTotal += parseFloat(row.querySelector("[data-prev-total]")?.dataset.prevTotal) || 0
      amountTotal += this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      faTotalsTotal += this._parseCurrency(row.querySelector("[data-field='fa-total']")?.textContent)

      const daysVal = row.querySelector("[data-field='days']")?.value?.trim() || ""
      if (daysVal.includes("(")) {
        daysTotal += parseInt(daysVal.replace(/[()]/g, "")) * -1
      } else {
        daysTotal += parseInt(daysVal) || 0
      }
    })

    if (this.hasFaModalContractTotalTarget) this.faModalContractTotalTarget.textContent = this._formatAmount(contractTotal)
    if (this.hasFaModalPrevTotalTarget) this.faModalPrevTotalTarget.textContent = this._formatAmount(prevTotal)
    if (this.hasFaModalTotalTarget) this.faModalTotalTarget.textContent = this._formatAmount(amountTotal)
    if (this.hasFaModalFaTotalsTotalTarget) this.faModalFaTotalsTotalTarget.textContent = this._formatAmount(faTotalsTotal)
    if (this.hasFaModalDaysTotalTarget) this.faModalDaysTotalTarget.textContent = daysTotal
  }

  hideNewFaModal() {
    this.newFaModalTarget.classList.add("hidden")
    this._attachmentIds = []
    this._pendingUploads = 0
    if (this.hasFaFileListTarget) this.faFileListTarget.innerHTML = ""
    showIntercom()
  }

  stopPropagation(e) {
    e.stopPropagation()
  }

  // ── File upload ─────────────────────────────────────────────────

  dragoverDropzone(event) {
    event.preventDefault()
    this.faDropzoneTarget.classList.add("border-brand", "bg-blue-50/50")
  }

  dragleaveDropzone() {
    this.faDropzoneTarget.classList.remove("border-brand", "bg-blue-50/50")
  }

  dropDropzone(event) {
    event.preventDefault()
    this.faDropzoneTarget.classList.remove("border-brand", "bg-blue-50/50")
    const files = Array.from(event.dataTransfer.files).filter(f =>
      f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    )
    if (files.length > 0) this._doFaFileUpload(files)
  }

  clickDropzone() {
    this.faFileInputTarget.click()
  }

  selectFiles(event) {
    const files = Array.from(event.target.files)
    if (files.length > 0) this._doFaFileUpload(files)
    event.target.value = ""
  }

  async _doFaFileUpload(files) {
    const token = this._csrfToken
    this._pendingUploads++
    this._updateUploadState()

    const placeholders = files.map(file => {
      const chip = this._createFileChip(file.name, { uploading: true })
      this.faFileListTarget.appendChild(chip)
      return chip
    })

    try {
      const presignUrl = `/projects/${this.projectIdValue}/finances/presign_uploads`
      const presignRes = await fetch(presignUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: files.map(f => ({
            filename: f.name,
            content_type: f.type || "application/pdf",
            byte_size: f.size
          }))
        })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to get upload URLs")
      }

      const { uploads } = await presignRes.json()

      const results = await Promise.allSettled(
        uploads.map(async (upload, i) => {
          const res = await fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": files[i].type || "application/pdf" },
            body: files[i]
          })
          if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)
          return upload
        })
      )

      const successful = results.filter(r => r.status === "fulfilled").map(r => r.value)
      if (successful.length === 0) throw new Error("All uploads failed")

      const registerUrl = `/projects/${this.projectIdValue}/finances/register_uploads`
      const registerRes = await fetch(registerUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(u => ({ cache_name: u.cache_name, filename: u.filename }))
        })
      })

      const registered = await registerRes.json()

      placeholders.forEach(p => p.remove())
      registered.forEach(a => {
        const name = a.filename || `File #${a.id}`
        this._attachmentIds.push(a.id)
        const chip = this._createFileChip(name, {
          removable: true,
          onRemove: () => {
            chip.remove()
            this._attachmentIds = this._attachmentIds.filter(id => id !== a.id)
          }
        })
        this.faFileListTarget.appendChild(chip)
      })
    } catch (e) {
      console.error("File upload failed:", e)
      placeholders.forEach(p => p.remove())
      this._showToast("File upload failed", "alert")
    } finally {
      this._pendingUploads--
      this._updateUploadState()
    }
  }

  _updateUploadState() {
    const uploading = this._pendingUploads > 0
    if (this.hasSaveDraftBtnTarget) this._setButtonUploading(this.saveDraftBtnTarget, uploading)
    if (this.hasSignRequestBtnTarget) this._setButtonUploading(this.signRequestBtnTarget, uploading)
    if (this.hasAddPastFaBtnTarget) this._setButtonUploading(this.addPastFaBtnTarget, uploading)
  }

  _setButtonUploading(btn, uploading) {
    btn.disabled = uploading
    btn.classList.toggle("opacity-60", uploading)
    btn.classList.toggle("cursor-not-allowed", uploading)
    btn.classList.toggle("pointer-events-none", uploading)
    if (uploading) {
      if (!btn.dataset.originalText) {
        btn.dataset.originalText = btn.textContent
        btn.style.minWidth = `${btn.offsetWidth}px`
      }
      btn.innerHTML = `<svg class="animate-spin h-4 w-4 inline-block align-middle mr-1 -mt-px" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>Uploading\u2026`
    } else if (btn.dataset.originalText) {
      btn.textContent = btn.dataset.originalText
      delete btn.dataset.originalText
      btn.style.minWidth = ""
    }
  }

  _createFileChip(name, { removable = false, uploading = false, onRemove = null, url = null } = {}) {
    const chip = document.createElement(url ? "a" : "div")
    if (url) {
      chip.href = url
      chip.target = "_blank"
      chip.rel = "noopener"
    }
    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? "pr-1" : "pr-2"} py-1 rounded-md text-xs${url ? " cursor-pointer hover:opacity-80" : ""}`
    chip.classList.add(uploading ? "chip-uploading" : "chip-file")

    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : `<svg class="w-3.5 h-3.5 shrink-0 chip-file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const truncatedName = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name

    let removeBtn = ""
    if (removable) {
      removeBtn = `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" data-remove-chip>
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    }

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${this._escAttr(name)}">${truncatedName}</span>${removeBtn}`

    if (removable && onRemove) {
      const btn = chip.querySelector("[data-remove-chip]")
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        onRemove()
      })
    }

    return chip
  }

  // ── History Log ────────────────────────────────────────────────

  _renderHistoryLog(entries) {
    if (!this.hasFaHistoryLogTarget) return
    this.faHistoryLogEntriesTarget.innerHTML = ""
    if (entries.length === 0) {
      this.faHistoryLogTarget.classList.add("hidden")
      return
    }
    entries.forEach(entry => {
      const p = document.createElement("p")
      p.className = "text-[13px] text-gray-400 leading-5"
      p.textContent = `${entry.datetime} - ${entry.username} (${entry.company_role}) ${entry.message}`
      this.faHistoryLogEntriesTarget.appendChild(p)
    })
    this.faHistoryLogTarget.classList.remove("hidden")
  }

  _renderModalHeaderBadges(data) {
    const isEditing = !!this._editSequence
    const signedCount = data.signed_count || 0
    const tooltip = data.signature_tooltip || ""
    const showRequestBtn = data.show_request_approval_btn || false
    const isPast = data.is_past_payment || false
    const myRoleSigned = isEditing && data.my_role_signed

    // Request Approval button — only when there are actual signatures
    if (this.hasModalRequestApprovalBtnTarget) {
      if (isEditing && showRequestBtn && signedCount > 0) {
        this._pendingSignSequence = this._editSequence
        this.modalRequestApprovalBtnTarget.classList.remove("hidden")
        this.modalRequestApprovalBtnTarget.classList.add("inline-flex")
      } else {
        this.modalRequestApprovalBtnTarget.classList.add("hidden")
        this.modalRequestApprovalBtnTarget.classList.remove("inline-flex")
      }
    }

    // Signed badge — amber for past, yellow/pending for owners at 1/2, green otherwise
    if (this.hasModalSignedBadgeTarget) {
      if (isEditing && (signedCount > 0 || isPast)) {
        this.modalSignedTextTarget.textContent = isPast ? "Past FA" : `${signedCount}/2 Signed`
        this.modalSignedCheckTarget.classList.add("hidden")
        if (!isPast && tooltip) {
          this.modalSignedTooltipTarget.setAttribute("data-tip", tooltip)
          this.modalSignedTooltipTarget.setAttribute("data-tip-align", "left")
          this.modalSignedTooltipTarget.setAttribute("data-tip-pos", "below")
          this.modalSignedTooltipTarget.classList.remove("hidden")
        } else {
          this.modalSignedTooltipTarget.classList.add("hidden")
        }
        if (!isPast && myRoleSigned) this.modalSignedCheckTarget.classList.remove("hidden")
        const badge = this.modalSignedBadgeTarget
        badge.classList.remove("bg-green-100", "text-green-600", "bg-yellow-50", "text-yellow-600", "bg-amber-100", "text-amber-600", "bg-gray-200", "text-gray-600")
        if (isPast) {
          badge.classList.add("bg-gray-200", "text-gray-600")
        } else if (signedCount < 2) {
          badge.classList.add("bg-amber-100", "text-amber-600")
        } else {
          badge.classList.add("bg-green-100", "text-green-600")
        }
        badge.classList.remove("hidden")
        badge.classList.add("inline-flex")
      } else {
        this.modalSignedBadgeTarget.classList.add("hidden")
        this.modalSignedBadgeTarget.classList.remove("inline-flex")
      }
    }

    // "Pending Vendor Signature" label — owners only, editing draft with 0 signatures
    if (this.hasModalPendingLabelTarget) {
      this.modalPendingLabelTarget.classList.toggle("hidden", !(this.isOwnerValue && isEditing && signedCount === 0 && !isPast))
    }

    // Delete button — vendors editing unsigned drafts, OR both roles on past FAs
    if (this.hasModalDeleteBtnTarget) {
      const showDelete = isEditing && ((this.isOtherVendorValue && signedCount === 0 && !isPast) || isPast)
      this.modalDeleteBtnTarget.classList.toggle("hidden", !showDelete)
    }

    // Edit Past FA button — both roles, past FAs only
    if (this.hasModalEditPastBtnTarget) {
      this.modalEditPastBtnTarget.classList.toggle("hidden", !isPast)
    }

    // Approve / Reject — owners only, when vendor has signed and owner hasn't (1/2)
    const showApproveReject = this.isOwnerValue && isEditing && signedCount >= 1 && !myRoleSigned && !isPast
    if (this.hasModalApproveBtnTarget) this.modalApproveBtnTarget.classList.toggle("hidden", !showApproveReject)

    // Reject — owners: show at 1/2 (pending approval) AND at 2/2 (fully signed, to revoke)
    const showReject = this.isOwnerValue && isEditing && signedCount >= 1 && !isPast
    if (this.hasModalRejectBtnTarget) this.modalRejectBtnTarget.classList.toggle("hidden", !showReject)

    // Sign & Request Approval — vendors only, when not signed, not past
    if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.classList.toggle("hidden", myRoleSigned || this.isOwnerValue || isPast)
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.classList.toggle("hidden", myRoleSigned || this.isOwnerValue || isPast)

    // Unsign — vendor only, when they signed but owner hasn't yet (1/2 only)
    const showUnsign = this.isOtherVendorValue && myRoleSigned && signedCount < 2
    if (this.hasUnsignBtnTarget) this.unsignBtnTarget.classList.toggle("hidden", !showUnsign)

    // Lock fields when signed
    const locked = myRoleSigned || isPast || (this.isOwnerValue && isEditing)
    this._setModalReadonly(locked)
  }

  _setModalReadonly(locked) {
    // Title, PM, PO fields
    const headerInputs = [this.faTitleTarget, this.faPmTarget, this.faPoTarget]
    headerInputs.forEach(input => {
      input.readOnly = locked
      input.classList.toggle("bg-yellow-50", !locked)
      input.classList.toggle("bg-transparent", locked)
    })

    // Line item editable fields (comment, amount, days)
    this.faLineItemsTarget.querySelectorAll("[data-field='comment'], [data-field='amount'], [data-field='days']").forEach(input => {
      input.readOnly = locked
      const td = input.closest("td")
      if (td) td.classList.toggle("bg-amber-50", !locked)
    })

    // Dropzone — hide entirely when locked, show only file chips
    if (this.hasFaDropzoneTarget) this.faDropzoneTarget.classList.toggle("hidden", locked)
    if (this.hasUploadLabelTarget) this.uploadLabelTarget.classList.toggle("hidden", locked)

    // Hide remove buttons on file chips when locked
    if (this.hasFaFileListTarget) {
      this.faFileListTarget.querySelectorAll("[data-remove-chip]").forEach(btn => {
        btn.classList.toggle("hidden", locked)
      })
    }
  }

  // ── Page card DOM helpers ──────────────────────────────────────

  _updateOverallTotals(overallTotal, overallDays) {
    if (this.hasOverallAmountTarget) {
      this.overallAmountTarget.innerHTML = `<b>${this._formatAmount(overallTotal)}</b>`
    }
    if (this.hasOverallDaysTarget) {
      this.overallDaysTarget.textContent = overallDays
    }
    if (this.hasOverallTotalsTarget) {
      this.overallTotalsTarget.classList.remove("hidden")
    }
  }

  _buildBadgeHTML(data) {
    const signedCount = data.signed_count || 0
    const isPast = data.is_past_payment || false
    const tooltip = data.signature_tooltip || ""
    const myRoleSigned = data.my_role_signed || false

    if (isPast) {
      return `<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">Past FA</span>`
    }

    if (signedCount > 0) {
      const badgeBg = signedCount < 2
        ? "bg-amber-100 text-amber-600"
        : "bg-green-100 text-green-600"

      let iconHtml = ""
      if (signedCount >= 2 && myRoleSigned) {
        iconHtml = SVG_CHECK
      }

      let tooltipHtml = ""
      if (tooltip) {
        tooltipHtml = `<span class="shrink-0 cursor-help flex items-center" data-tip="${this._escAttr(tooltip)}" data-tip-align="left" data-tip-pos="below">${SVG_INFO_CIRCLE}</span>`
      }

      return `<span class="inline-flex items-center gap-1 ${badgeBg} text-[12px] font-medium rounded-lg px-2.5 py-[2px]">${iconHtml} ${signedCount}/2 Signed ${tooltipHtml}</span>`
    }

    return `<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">DRAFT</span>`
  }

  _buildCardLineItemsHTML(lineItems) {
    const cur = this.currencyValue
    let html = ""
    lineItems.forEach(li => {
      const amount = li.amount || 0
      let amountHtml
      if (amount >= 0) {
        amountHtml = `<span class="flex-1 pr-2 text-right">${this._formatAmount(amount)}</span>`
      } else {
        amountHtml = `<span class="flex-1 pr-2 text-right text-red-600">(${this._formatAmount(Math.abs(amount))})</span>`
      }

      const days = li.days || ""
      const isNegDays = days.includes && days.includes("(")
      const daysCls = isNegDays ? "px-2 py-1 text-right text-red-600" : "px-2 py-1 text-right"

      html += `<tr class="bg-white border-b border-l border-r border-gray-200">
        <td class="px-2 py-1 border-r border-gray-200 truncate">${this._escAttr(li.item || "")}</td>
        <td class="px-2 py-1 border-r border-gray-200 truncate">${this._escAttr(li.work || "")}</td>
        <td class="px-2 py-1 border-r border-gray-200 truncate">${this._escAttr(li.comment || "")}</td>
        <td class="px-0 py-1 border-r border-gray-200">
          <div class="flex items-center">
            <span class="pl-2 text-gray-400 select-none text-[13px]">${cur}</span>
            ${amountHtml}
          </div>
        </td>
        <td class="${daysCls}">${this._escAttr(days)}</td>
      </tr>`
    })
    return html
  }

  _buildCardTotalRowHTML(faTotal, faDays) {
    const cur = this.currencyValue
    let amountHtml
    if (faTotal >= 0) {
      amountHtml = `<span class="flex-1 pr-2 text-right"><b>${this._formatAmount(faTotal)}</b></span>`
    } else {
      amountHtml = `<span class="flex-1 pr-2 text-right text-red-600"><b>(${this._formatAmount(Math.abs(faTotal))})</b></span>`
    }

    let daysHtml
    if (faDays >= 0) {
      daysHtml = `<td data-card-total-days class="px-2 py-1 text-right font-medium">${faDays}</td>`
    } else {
      daysHtml = `<td data-card-total-days class="px-2 py-1 text-right text-red-600 font-medium">(${Math.abs(faDays)})</td>`
    }

    return `<tr class="bg-white border-b border-l border-r border-gray-200">
      <td colspan="3" class="px-2 py-1 text-right border-r border-gray-200"><b>Total</b></td>
      <td data-card-total-amount class="px-0 py-1 border-r border-gray-200">
        <div class="flex items-center">
          <span class="pl-2 text-gray-400 select-none text-[13px]">${cur}</span>
          ${amountHtml}
        </div>
      </td>
      ${daysHtml}
    </tr>`
  }

  _updatePageCard(data) {
    const card = this.element.querySelector(`.fee-adjustment[data-sequence="${data.sequence}"]`)
    if (!card) return

    card.dataset.signedCount = data.signed_count || 0
    card.dataset.isPast = data.is_past_payment || false

    // Badge
    const badgeArea = card.querySelector("[data-badge-area]")
    if (badgeArea) badgeArea.innerHTML = this._buildBadgeHTML(data)

    // Last updated
    const lastUpdated = card.querySelector("[data-last-updated]")
    if (lastUpdated && data.last_updated_at) {
      lastUpdated.textContent = `Last updated on ${data.last_updated_at} by ${data.who_updated || ""}`
    }

    // Total amount
    const totalAmount = card.querySelector("[data-card-total-amount]")
    if (totalAmount) {
      totalAmount.textContent = `${this.currencyValue}${this._formatAmount(data.fa_total || 0)}`
    }
  }

  _buildFullCardHTML(data) {
    const seq = data.sequence
    const isPast = data.is_past_payment || false
    const pdfUrl = `/projects/${this.projectIdValue}/finances/ovfa_export_with_pdfs/${seq}.pdf?ovid=${this.ovidValue}`
    const faTotal = data.fa_total || 0
    const cur = this.currencyValue

    return `<div class="fee-adjustment flex items-center justify-between px-3 py-2.5 text-[13px] text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" data-sequence="${seq}" data-signed-count="${data.signed_count || 0}" data-is-past="${isPast}" data-action="click->ov-fee-adjustment#editFeeAdjustment">
      <div class="flex items-center gap-3">
        <span class="font-semibold text-gray-700 text-[14px]">Fee Adjustment #${seq}</span>
        <span data-badge-area>${this._buildBadgeHTML(data)}</span>
      </div>
      <div class="flex items-center gap-3">
        <div class="relative" data-controller="dropdown">
          <button data-action="click->dropdown#toggle" class="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px] transition-colors cursor-pointer">Export <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z"/></svg></button>
          <div data-dropdown-target="menu" class="hidden absolute right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-[100px] py-1">
            <a href="${pdfUrl}" target="_blank" data-action="click->ov-fee-adjustment#stopPropagation" class="block px-3 py-1 text-[13px] text-gray-700 hover:bg-gray-50">PDF</a>
          </div>
        </div>
        <span data-last-updated class="text-gray-400 text-[12px] italic">Last updated on ${data.last_updated_at || ""} by ${data.who_updated || ""}</span>
        <span data-card-total-amount class="font-semibold text-gray-700 text-[14px]">${cur}${this._formatAmount(faTotal)}</span>
      </div>
    </div>`
  }

  _insertNewPageCard(data) {
    // Hide empty state, show overall totals
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")

    const cardsContainer = this.hasFaCardsTarget ? this.faCardsTarget : null
    if (!cardsContainer) return

    // Ensure flex layout on container
    cardsContainer.classList.add("flex", "flex-col", "gap-2")

    const newCardHTML = this._buildFullCardHTML(data)
    const newSeq = data.sequence

    // Insert in descending sequence order
    const existingCards = cardsContainer.querySelectorAll(".fee-adjustment[data-sequence]")
    let inserted = false
    for (const card of existingCards) {
      const cardSeq = parseInt(card.dataset.sequence)
      if (newSeq > cardSeq) {
        card.insertAdjacentHTML("beforebegin", newCardHTML)
        inserted = true
        break
      }
    }
    if (!inserted) {
      cardsContainer.insertAdjacentHTML("beforeend", newCardHTML)
    }
  }

  _removePageCard(sequence) {
    const card = this.element.querySelector(`.fee-adjustment[data-sequence="${sequence}"]`)
    if (card) card.remove()
  }

  _updateModalState(data) {
    this._renderModalHeaderBadges(data)
    this._renderHistoryLog(data.history_log || [])
  }

  // ── Add Past FA ─────────────────────────────────────────────────

  async addPastFa() {
    this._clearValidationErrors()
    let valid = true

    // Title required
    if (!this.faTitleTarget.value.trim()) {
      this._markFieldError(this.faTitleTarget)
      valid = false
    }

    // Comment required for rows with amount/days
    let missingComments = false
    this.faLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      const days = row.querySelector("[data-field='days']")?.value?.trim() || ""
      const comment = row.querySelector("[data-field='comment']")?.value?.trim() || ""
      const commentInput = row.querySelector("[data-field='comment']")
      if ((amount !== 0 || days) && !comment) {
        missingComments = true
        if (commentInput) this._markFieldError(commentInput)
      }
    })

    if (!valid || missingComments) {
      this._showToast("Please enter a Title, and ensure all rows with a value in Amount or Days include a Comment.", "alert")
      return
    }

    const lineItemsArr = this._collectFaLineItems()
    if (lineItemsArr.length === 0) {
      this._showToast("At least one line item with amount or days is required. They must match the PDF.", "alert")
      return
    }

    // Amount format validation
    let badFormat = false
    this.faLineItemsTarget.querySelectorAll("[data-field='amount']").forEach(el => {
      const raw = el.value.trim()
      if (!raw) return
      let cleaned = raw
      if (this.currencyFormatValue === "european_currency_format") {
        cleaned = cleaned.replace(/\./g, "").replace(",", ".")
      } else if (this.currencyFormatValue === "swiss_currency_format") {
        cleaned = cleaned.replace(/'/g, "")
      } else {
        cleaned = cleaned.replace(/,/g, "")
      }
      cleaned = cleaned.replace(this.currencyValue, "").trim()
      if (!/^\(?-?\d*\.?\d*\)?$/.test(cleaned) || cleaned === "" || cleaned === "." || cleaned === "()" || cleaned === "-.") {
        badFormat = true
        this._markFieldError(el)
      }
    })
    if (badFormat) {
      this._showToast("One or more amount fields are formatted incorrectly. Use numbers like 100.50, -100.50, or (100.50).", "alert")
      return
    }

    // Deduction overflow
    let deductionOverflow = false
    this.faLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      if (amount === 0) return
      const contractAmount = this._parseCurrency(row.querySelector("[data-field='contract']")?.textContent)
      const faTotal = this._parseCurrency(row.querySelector("[data-field='fa-total']")?.textContent)
      if (Math.round((contractAmount + faTotal) * 100) / 100 < 0) deductionOverflow = true
    })
    if (deductionOverflow) {
      this._showToast("One or more amounts resulted in a total that exceeds the contract amount. Please correct and try again.", "alert")
      return
    }

    // Check all previous non-past FAs are fully signed (2/2)
    const faElements = this.element.querySelectorAll(".fee-adjustment[data-sequence]")
    for (const el of faElements) {
      if (el.dataset.isPast === "true") continue
      const signedCount = parseInt(el.dataset.signedCount) || 0
      if (signedCount < 2) {
        this._showToast("All previous fee adjustments must be fully signed (2/2) before adding a Past FA", "alert")
        return
      }
    }

    // Require at least one backup PDF
    if (this._attachmentIds.length === 0) {
      this._showToast("At least one PDF is required for a Past FA", "alert")
      return
    }

    // Show confirmation modal
    const nextSequence = await this._getNextFaSequence()
    if (this.hasPastFaSequenceLabelTarget) {
      this.pastFaSequenceLabelTarget.textContent = `FA#${nextSequence}`
    }
    this.pastFaConfirmModalTarget.classList.remove("hidden")
  }

  async confirmPastFa() {
    this.pastFaConfirmModalTarget.classList.add("hidden")
    this._isPast = true
    this._skipValidation = true
    await this._submitFa(false)
    this._skipValidation = false
  }

  hidePastFaConfirmModal() {
    this.pastFaConfirmModalTarget.classList.add("hidden")
  }

  // ── Save FA ───────────────────────────────────────────────────────

  async saveDraft() { await this._submitFa(false) }
  async signAndRequest() { await this._submitFa(true) }

  _validateFa(signAfter) {
    this._clearValidationErrors()
    let valid = true

    // Title required (always)
    if (!this.faTitleTarget.value.trim()) {
      this._markFieldError(this.faTitleTarget)
      valid = false
    }

    // Comment required for rows with amount or days
    let missingComments = false
    this.faLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      const days = row.querySelector("[data-field='days']")?.value?.trim() || ""
      const comment = row.querySelector("[data-field='comment']")?.value?.trim() || ""
      const commentInput = row.querySelector("[data-field='comment']")
      if ((amount !== 0 || days) && !comment) {
        missingComments = true
        if (commentInput) this._markFieldError(commentInput)
      }
    })

    if (!valid && missingComments) {
      this._showToast("Please enter a Title, and ensure all rows with a value in Amount or Days include a Comment.", "alert")
      return false
    } else if (!valid) {
      this._showToast("Title is required.", "alert")
      return false
    } else if (missingComments) {
      this._showToast("All rows with a value in Amount or Days must include a Comment.", "alert")
      return false
    }

    // Amount format validation
    let badFormat = false
    this.faLineItemsTarget.querySelectorAll("[data-field='amount']").forEach(el => {
      const raw = el.value.trim()
      if (!raw) return
      let cleaned = raw
      if (this.currencyFormatValue === "european_currency_format") {
        cleaned = cleaned.replace(/\./g, "").replace(",", ".")
      } else if (this.currencyFormatValue === "swiss_currency_format") {
        cleaned = cleaned.replace(/'/g, "")
      } else {
        cleaned = cleaned.replace(/,/g, "")
      }
      cleaned = cleaned.replace(this.currencyValue, "").trim()
      if (!/^\(?-?\d*\.?\d*\)?$/.test(cleaned) || cleaned === "" || cleaned === "." || cleaned === "()" || cleaned === "-.") {
        badFormat = true
        this._markFieldError(el)
      }
    })
    if (badFormat) {
      this._showToast("One or more amount fields are formatted incorrectly. Use numbers like 100.50, -100.50, or (100.50).", "alert")
      return false
    }

    // Deduction overflow: FA total (previous + current amount) can't go below zero
    let deductionOverflow = false
    this.faLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      if (amount === 0) return
      const contractAmount = this._parseCurrency(row.querySelector("[data-field='contract']")?.textContent)
      const faTotalText = row.querySelector("[data-field='fa-total']")?.textContent
      const faTotal = this._parseCurrency(faTotalText)
      if (Math.round((contractAmount + faTotal) * 100) / 100 < 0) deductionOverflow = true
    })
    if (deductionOverflow) {
      this._showToast("One or more amounts resulted in a total that exceeds the contract amount. Please correct and try again.", "alert")
      return false
    }

    // Sign-only validations
    if (signAfter) {
      // At least one row must have an amount
      let hasAmount = false
      this.faLineItemsTarget.querySelectorAll("[data-field='amount']").forEach(el => {
        if (this._parseCurrency(el.value) !== 0) hasAmount = true
      })
      if (!hasAmount) {
        this._showToast("At least one line item must have an amount.", "alert")
        return false
      }

      // At least one backup PDF required
      if (this._attachmentIds.length === 0) {
        this._showToast("At least one PDF is required to sign.", "alert")
        return false
      }

      // All PREVIOUS non-past FAs must be fully signed (2/2). Only the ones
      // numbered before this one count — a draft started for a later FA
      // used to block signing this one (Robert Conklin, 2026-09-14).
      const mine = this._editSequence ? parseInt(this._editSequence, 10) : Infinity
      const faElements = this.element.querySelectorAll(".fee-adjustment[data-sequence]")
      for (const el of faElements) {
        const seq = parseInt(el.dataset.sequence, 10)
        if (!(seq < mine)) continue
        if (el.dataset.isPast === "true") continue
        const signedCount = parseInt(el.dataset.signedCount) || 0
        if (signedCount < 2) {
          this._showToast("All previous fee adjustments must be fully signed (2/2) before signing this one.", "alert")
          return false
        }
      }
    }

    return true
  }

  _markFieldError(input) {
    input.classList.add("!bg-red-100")
    input.addEventListener("input", () => input.classList.remove("!bg-red-100"), { once: true })
  }

  _clearValidationErrors() {
    this.newFaModalTarget.querySelectorAll(".\\!bg-red-100").forEach(el => {
      el.classList.remove("!bg-red-100")
    })
  }

  async _submitFa(signAfter) {
    if (this._submitting) return

    const lineItemsArr = this._collectFaLineItems()
    if (lineItemsArr.length === 0 && !signAfter) return

    if (!this._skipValidation && !this._validateFa(signAfter)) return

    if (lineItemsArr.length === 0) return

    this._submitting = true
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = true
    if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = true

    try {

    const sequence = this._editSequence || await this._getNextFaSequence()
    const isUpdate = !!this._editSequence

    let action
    if (isUpdate) {
      action = this._isPast ? "update_past_ov_fee_adjustment_line_items" : "update_ov_fee_adjustment_line_items"
    } else {
      action = this._isPast ? "create_past_ov_fee_adjustment_line_items" : "create_ov_fee_adjustment_line_items"
    }

    const params = {
      sequence: sequence,
      line_items_arr: JSON.stringify(lineItemsArr),
      title: this.faTitleTarget.value,
      project_manager: this.faPmTarget.value,
      po_number: this.faPoTarget.value,
      attachment_ids: this._attachmentIds.join(" "),
      ovid: this.ovidValue
    }

    const method = isUpdate ? "PUT" : "POST"
    const saveResp = await this._ajax(method, action, params)
    if (!saveResp) return

    if (signAfter && !this._isPast) {
      // Show digital signature acknowledge modal before signing
      this._pendingSignSequence = sequence
      this._signFromEditModal = true
      this._signFromEditIsNew = !isUpdate
      this._signFromEditSaveResp = saveResp
      this.signModalTarget.classList.remove("hidden")
      return
    }

    // Update page card (save without sign)
    if (isUpdate) {
      this._updatePageCard(saveResp)
    } else {
      this._insertNewPageCard(saveResp)
    }
    this._updateOverallTotals(saveResp.overall_total, saveResp.overall_days)

    this.hideNewFaModal()
    this._showToast("Saved", "success")
    } finally {
      this._submitting = false
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = false
      if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = false
    }
  }

  _collectFaLineItems() {
    const items = []
    this.faLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const lineItemId = row.dataset.lineItemId
      const amount = this._parseCurrency(row.querySelector("[data-field='amount']")?.value)
      const days = row.querySelector("[data-field='days']")?.value || ""
      const comment = row.querySelector("[data-field='comment']")?.value || ""
      if (amount !== 0 || days || comment) {
        items.push([lineItemId, amount, days, comment])
      }
    })
    return items
  }

  async _getNextFaSequence() {
    const faElements = this.element.querySelectorAll(".fee-adjustment[data-sequence]")
    let max = 0
    faElements.forEach(el => {
      const seq = parseInt(el.dataset.sequence)
      if (seq > max) max = seq
    })
    return max + 1
  }

  // ── Delete ────────────────────────────────────────────────────────

  showDeleteModal(sequence) {
    const cards = this.element.querySelectorAll(".fee-adjustment[data-sequence]")
    let maxSeq = 0
    cards.forEach(el => { const s = parseInt(el.dataset.sequence); if (s > maxSeq) maxSeq = s })
    if (parseInt(sequence) !== maxSeq) {
      this._showToast("Must delete the most recent item first. To delete this item, please first delete any more recent records.", "alert")
      return
    }
    this._pendingDeleteSequence = sequence
    this.deleteModalTarget.classList.remove("hidden")
  }
  hideDeleteModal() { this.deleteModalTarget.classList.add("hidden") }

  async confirmDelete() {
    const resp = await this._ajax("DELETE", "destroy_ov_fee_adjustment_line_items", { sequence: this._pendingDeleteSequence, ovid: this.ovidValue })
    this.hideDeleteModal()
    if (!resp) return
    this.hideNewFaModal()
    this._removePageCard(this._pendingDeleteSequence)

    if (resp.has_any_fas) {
      this._updateOverallTotals(resp.overall_total, resp.overall_days)
    } else {
      if (this.hasOverallTotalsTarget) this.overallTotalsTarget.classList.add("hidden")
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    }
    this._showToast("Deleted", "success")
  }

  // ── Sign / Unsign ─────────────────────────────────────────────────

  showSignModal(sequence) {
    this._pendingSignSequence = sequence
    this.signModalTarget.classList.remove("hidden")
  }
  hideSignModal() {
    // When in edit-modal sign flow or approve flow, Cancel goes back to the edit modal
    if (this._signFromEditModal || this._approveAfterAcknowledge) {
      this.signModalTarget.classList.add("hidden")
      this._signFromEditModal = false
      this._signFromEditIsNew = false
      this._signFromEditSaveResp = null
      this._approveAfterAcknowledge = false
      return
    }
    this.signModalTarget.classList.add("hidden")
  }

  async confirmSign(event) {
    // One request per gesture — key-repeat (held Enter) or double-clicks fire
    // this repeatedly while the request is still in flight.
    if (this._confirmInFlight) return
    this._confirmInFlight = true
    const confirmBtn = event?.currentTarget
    if (confirmBtn) confirmBtn.disabled = true
    try {
      await this._confirmSignInner()
    } finally {
      this._confirmInFlight = false
      if (confirmBtn) confirmBtn.disabled = false
    }
  }

  async _confirmSignInner() {
    const fromEditModal = this._signFromEditModal
    const isNew = this._signFromEditIsNew
    const fromApprove = this._approveAfterAcknowledge
    this._signFromEditModal = false
    this._signFromEditIsNew = false
    this._signFromEditSaveResp = null
    this._approveAfterAcknowledge = false

    const resp = await this._ajax("POST", "sign_ovfa", { sequence: this._pendingSignSequence, ovid: this.ovidValue })
    this.signModalTarget.classList.add("hidden")
    if (!resp) return

    if (fromApprove) {
      this._updatePageCard(resp)
      this._updateModalState(resp)
      this.hideNewFaModal()
      this._showToast("Approved", "success")
      return
    }

    if (fromEditModal) {
      // Flow: vendor clicked Sign in edit modal -> saved -> acknowledged signature -> now signing
      if (isNew) {
        this._insertNewPageCard(resp)
      } else {
        this._updatePageCard(resp)
      }
      this._updateOverallTotals(resp.overall_total, resp.overall_days)
      this._updateModalState(resp)

      // Show request approval modal for vendors
      if (this.isOtherVendorValue) {
        this._signedBeforeRequest = true
        this.requestApprovalModalTarget.classList.remove("hidden")
      } else {
        this.hideNewFaModal()
      }
      this._showToast("Signed", "success")
      return
    }

    // Standalone sign from page card badge
    this._updatePageCard(resp)
    this._showToast("Signed", "success")
  }

  unsignFromModal() {
    this._pendingSignSequence = this._editSequence
    this.unsignModalTarget.classList.remove("hidden")
  }

  showUnsignModal(sequence) {
    this._pendingSignSequence = sequence
    this.unsignModalTarget.classList.remove("hidden")
  }
  hideUnsignModal() { this.unsignModalTarget.classList.add("hidden") }

  async confirmUnsign() {
    const resp = await this._ajax("POST", "unsign_ovfa", { sequence: this._pendingSignSequence, ovid: this.ovidValue })
    this.hideUnsignModal()
    if (!resp) return
    this._updatePageCard(resp)
    this._updateModalState(resp)
    this._showToast("Signature removed", "success")
  }

  // ── Edit Past FA from modal ────────────────────────────────────────

  editPastFa() {
    this._setModalReadonly(false)
    if (this.hasModalEditPastBtnTarget) this.modalEditPastBtnTarget.classList.add("hidden")
    if (this.hasSaveDraftBtnTarget) {
      this.saveDraftBtnTarget.classList.remove("hidden")
      this.saveDraftBtnTarget.textContent = "Update Past FA"
    }
  }

  // ── Delete from modal ──────────────────────────────────────────────

  deleteFromModal() {
    const cards = this.element.querySelectorAll(".fee-adjustment[data-sequence]")
    let maxSeq = 0
    cards.forEach(el => { const s = parseInt(el.dataset.sequence); if (s > maxSeq) maxSeq = s })
    if (parseInt(this._editSequence) !== maxSeq) {
      this._showToast("Must delete the most recent item first. To delete this item, please first delete any more recent records.", "alert")
      return
    }
    this._pendingDeleteSequence = this._editSequence
    this.deleteModalTarget.classList.remove("hidden")
  }

  // ── Approve / Reject ───────────────────────────────────────────────

  approveFa() {
    this._pendingSignSequence = this._editSequence
    this._approveAfterAcknowledge = true
    this.signModalTarget.classList.remove("hidden")
  }

  showRejectFaModal() { this.rejectFaModalTarget.classList.remove("hidden") }
  hideRejectFaModal() {
    this.rejectFaModalTarget.classList.add("hidden")
    if (this.hasRejectCommentTarget) this.rejectCommentTarget.value = ""
  }

  async confirmRejectFa() {
    const comment = this.hasRejectCommentTarget ? this.rejectCommentTarget.value : ""
    const resp = await this._ajax("POST", "reject_ovfa", { sequence: this._editSequence, comment, ovid: this.ovidValue })
    this.hideRejectFaModal()
    if (!resp) return
    this._updatePageCard(resp)
    this._updateModalState(resp)
    this.hideNewFaModal()
    this._showToast("Rejected", "success")
  }

  // ── Request approval ──────────────────────────────────────────────

  showRequestApprovalModal() { this.requestApprovalModalTarget.classList.remove("hidden") }
  hideRequestApprovalModal() {
    this.requestApprovalModalTarget.classList.add("hidden")
    // If signing already happened, just close the modal — page card was already updated
    if (this._signedBeforeRequest) {
      this._signedBeforeRequest = false
      this.hideNewFaModal()
    }
  }

  async sendApprovalRequest() {
    const sections = this.requestApprovalModalTarget.querySelectorAll("[data-company-section]")
    for (const section of sections) {
      if (section.classList.contains("hidden")) continue
      const checked = section.querySelectorAll("input[name='approval_email']:checked")
      if (checked.length === 0) {
        this._showToast("Please select at least one person from each company.", "alert")
        return
      }
    }

    const checkboxes = this.requestApprovalModalTarget.querySelectorAll("input[name='approval_email']:checked")
    const emails = Array.from(checkboxes).map(cb => cb.dataset.email)
    if (emails.length === 0) return

    const resp = await this._ajax("POST", "request_ovfa_approval", {
      sequence: this._pendingSignSequence,
      emails: JSON.stringify(emails),
      ovid: this.ovidValue
    })
    if (!resp) return
    this._updatePageCard(resp)
    this._signedBeforeRequest = false
    this.requestApprovalModalTarget.classList.add("hidden")
    this.hideNewFaModal()
    this._showToast("Approval request sent", "success")
  }

  // ── Breadcrumb count ──────────────────────────────────────────────

  _updateBreadcrumbCount(count) {
    const el = document.getElementById("ovfa-breadcrumb-count")
    if (el) el.textContent = count
  }

  // ── Toast ─────────────────────────────────────────────────────────

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.style.zIndex = "9999"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── AJAX helper ───────────────────────────────────────────────────

  async _ajax(method, action, params = {}) {
    const url = `/projects/${this.projectIdValue}/finances/${action}`
    const body = new FormData()
    Object.entries(params).forEach(([k, v]) => body.append(k, v))

    const options = {
      method: method === "GET" ? "GET" : "POST",
      headers: { "X-CSRF-Token": this._csrfToken },
    }

    if (method !== "GET") {
      if (method !== "POST") body.append("_method", method.toLowerCase())
      options.body = body
    }

    try {
      const resp = await fetch(url, options)
      if (!resp.ok) {
        this._showToast("Something went wrong. Please try again.", "alert")
        return null
      }
      if (resp.headers.get("content-type")?.includes("application/json")) {
        return await resp.json()
      }
      return true
    } catch (e) {
      console.error("Fee adjustment request failed:", e)
      this._showToast("Something went wrong. Please try again.", "alert")
      return null
    }
  }
}
